import { useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Phone, Mail, Dog, Clock, AlertTriangle, GripVertical, Calendar as CalendarIcon, PhoneCall } from "lucide-react";
import SelfRegisteredBadge from "./SelfRegisteredBadge";

const OUTREACH_TYPES = new Set(["call", "called", "left_voicemail", "email", "email_sent", "sms"]);
const CONTACT_TYPES = new Set(["call", "called", "left_voicemail", "email", "email_sent", "sms"]);

// Pipeline columns (Lost / Not-a-Lead intentionally excluded — List view only)
const COLUMNS = [
  { key: "new", label: "New", header: "keep-color bg-gradient-to-r from-[#7FD3C6] to-[#5CC4B4]", ring: "ring-[#5CC4B4]/50", soft: "bg-[#F5F9F9]" },
  { key: "contacted", label: "Contacted", header: "keep-color bg-gradient-to-r from-[#4DB8A9] to-[#2BA597]", ring: "ring-[#2BA597]/50", soft: "bg-[#F5F9F9]" },
  { key: "qualified", label: "Qualified", header: "keep-color bg-gradient-to-r from-[#2BA597] to-[#1F8A7C]", ring: "ring-[#1F8A7C]/50", soft: "bg-[#F5F9F9]" },
  { key: "converted", label: "Converted", header: "keep-color bg-gradient-to-r from-[#1F8A7C] to-[#14594F]", ring: "ring-[#1F8A7C]/50", soft: "bg-[#F5F9F9]" },
  { key: "closed_no_response", label: "Closed \u2013 No Response", header: "keep-color bg-gradient-to-r from-[#7B8D96] to-[#5B7480]", ring: "ring-gray-300", soft: "bg-[#F5F9F9]" },
];

const CAP_OPTIONS = [20, 50, "all"];

const outreachOf = (l) => {
  if (typeof l.outreach_count === "number") return l.outreach_count;
  return (l.action_log || []).filter((a) => OUTREACH_TYPES.has(a.type)).length;
};

const lastActionOf = (l) => {
  const log = l.action_log || [];
  if (!log.length) return null;
  return [...log].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
};

const lastContactOf = (l) => {
  const log = l.action_log || [];
  if (!log.length) return null;
  const sorted = [...log].sort((a, b) => new Date(b.date) - new Date(a.date));
  return sorted.find((a) => CONTACT_TYPES.has(a.type)) || null;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor(diff / 3600000);
  if (days > 30) return `${Math.floor(days / 30)}mo ago`;
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return "Just now";
};

const ageDays = (dateStr) => {
  if (!dateStr) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000));
};

const LeadCard = ({ lead, index, onCardClick, columnKey }) => {
  const attempts = outreachOf(lead);
  const last = lastActionOf(lead);
  const lastContact = lastContactOf(lead);
  const ago = last ? timeAgo(last.date) : null;
  const isCall = last && (last.type || "").includes("call");
  const isEmail = last && (last.type || "").includes("email");
  const age = ageDays(lead.created_at);
  const highlightSelfReg = lead.self_registered && columnKey === "qualified";

  return (
    <Draggable draggableId={lead.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onCardClick(lead.id)}
          data-testid={`kanban-card-${lead.id}`}
          className={`group bg-white rounded-lg border p-3 mb-2 cursor-pointer shadow-sm transition-shadow hover:shadow-md ${
            snapshot.isDragging ? "shadow-xl ring-2 ring-amber-300 rotate-1" : ""
          } ${highlightSelfReg ? "border-emerald-300 ring-1 ring-emerald-200" : "border-gray-200"}`}
        >
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-bold text-gray-900 leading-snug truncate" style={{ fontFamily: "Manrope" }}>
              {lead.name || "Unknown Lead"}
            </h4>
            <GripVertical className="h-4 w-4 text-gray-300 group-hover:text-gray-400 shrink-0" />
          </div>

          {lead.self_registered && (
            <div className="mt-1.5" data-testid={`kanban-selfreg-${lead.id}`}>
              <SelfRegisteredBadge size="sm" withPulse={highlightSelfReg} />
            </div>
          )}

          {/* Contact snippet */}
          {(lead.canonical_email || lead.primary_phone) && (
            <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-gray-500 truncate">
              {lead.primary_phone ? <Phone className="h-3 w-3 shrink-0" /> : <Mail className="h-3 w-3 shrink-0" />}
              <span className="truncate">{lead.primary_phone || lead.canonical_email}</span>
            </div>
          )}

          {(lead.pet_name || lead.service) && (
            <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 truncate">
              <Dog className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {[lead.pet_name, lead.service].filter(Boolean).join(" \u00b7 ")}
              </span>
            </div>
          )}

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {/* Score chip */}
            <span className="inline-flex items-center text-[11px] font-semibold text-cyan-700 bg-cyan-50 px-1.5 py-0.5 rounded">
              Score {lead.score ?? 0}
            </span>
            {/* Age chip */}
            {age !== null && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded"
                title="Days since lead was created"
                data-testid={`kanban-age-${lead.id}`}
              >
                <CalendarIcon className="h-3 w-3" />
                {age}d
              </span>
            )}
            {/* X/5 outreach counter */}
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                attempts >= 5 ? "text-rose-700 bg-rose-100" : attempts >= 3 ? "text-amber-700 bg-amber-100" : "text-gray-600 bg-gray-100"
              }`}
              data-testid={`kanban-outreach-${lead.id}`}
              title="Logged outreach attempts (Call/Email/SMS)"
            >
              {attempts >= 5 && <AlertTriangle className="h-3 w-3" />}
              {Math.min(attempts, 5)}/5 attempts
            </span>
          </div>

          {/* Last contact + last activity line */}
          <div className="flex items-center gap-2 mt-2 text-[11px] text-gray-500 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <PhoneCall className="h-3 w-3" />
              {lastContact ? `Contact ${timeAgo(lastContact.date)}` : "Never contacted"}
            </span>
            <span className="inline-flex items-center gap-1">
              {isCall ? <Phone className="h-3 w-3 text-blue-500" /> : isEmail ? <Mail className="h-3 w-3 text-green-500" /> : <Clock className="h-3 w-3" />}
              {ago ? ago : "No activity"}
            </span>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export const LeadsKanban = ({ leads, searchTerm, onStatusChange, onCardClick }) => {
  const [convertedCap, setConvertedCap] = useState(() => {
    const raw = localStorage.getItem("leads_kanban_converted_cap");
    if (raw === "all") return "all";
    const n = parseInt(raw, 10);
    return [20, 50].includes(n) ? n : 20;
  });

  const changeCap = (val) => {
    setConvertedCap(val);
    localStorage.setItem("leads_kanban_converted_cap", String(val));
  };

  const byColumn = useMemo(() => {
    const s = (searchTerm || "").toLowerCase();
    const filtered = leads.filter(
      (l) =>
        !s ||
        l.name?.toLowerCase().includes(s) ||
        l.canonical_email?.toLowerCase().includes(s) ||
        l.company?.toLowerCase().includes(s)
    );
    const map = {};
    COLUMNS.forEach((c) => (map[c.key] = []));
    filtered.forEach((l) => {
      if (map[l.status]) map[l.status].push(l);
    });
    Object.keys(map).forEach((k) => {
      // Self-registered leads float to the top of every column, then most recent first
      map[k].sort((a, b) => {
        const aReg = a.self_registered ? 0 : 1;
        const bReg = b.self_registered ? 0 : 1;
        if (aReg !== bReg) return aReg - bReg;
        return new Date(b.created_at) - new Date(a.created_at);
      });
    });
    return map;
  }, [leads, searchTerm]);

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;
    onStatusChange(draggableId, destination.droppableId);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="leads-kanban-board">
        {COLUMNS.map((col) => {
          const items = byColumn[col.key];
          const isConverted = col.key === "converted";
          const capped = isConverted && convertedCap !== "all"
            ? items.slice(0, convertedCap)
            : items;
          const hidden = items.length - capped.length;
          return (
            <div key={col.key} className="flex-shrink-0 w-72 flex flex-col" data-testid={`kanban-column-${col.key}`}>
              <div className={`${col.header} text-white rounded-t-lg px-3 py-2 flex items-center justify-between`}>
                <span className="text-sm font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>{col.label}</span>
                <span className="text-xs font-semibold bg-white/25 rounded-full px-2 py-0.5" data-testid={`kanban-count-${col.key}`}>
                  {isConverted && convertedCap !== "all" && items.length > capped.length
                    ? `${capped.length} of ${items.length}`
                    : items.length}
                </span>
              </div>

              {isConverted && (
                <div className="bg-emerald-50/60 border-x border-emerald-100 px-2 py-1.5 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-emerald-800 font-semibold">Show recent</span>
                  <div className="inline-flex rounded-md overflow-hidden border border-emerald-200" data-testid="converted-cap-toggle">
                    {CAP_OPTIONS.map((opt) => (
                      <button
                        key={String(opt)}
                        onClick={() => changeCap(opt)}
                        className={`px-2 py-0.5 text-[11px] font-bold transition-colors ${
                          String(convertedCap) === String(opt)
                            ? "bg-emerald-600 text-white"
                            : "bg-white text-emerald-700 hover:bg-emerald-100"
                        }`}
                        data-testid={`converted-cap-${opt}`}
                      >
                        {opt === "all" ? "All" : opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Droppable droppableId={col.key}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 min-h-[60vh] p-2 rounded-b-lg border border-t-0 border-gray-200 transition-colors ${
                      snapshot.isDraggingOver ? `${col.soft} ring-2 ${col.ring}` : "bg-gray-50/60"
                    }`}
                  >
                    {items.length === 0 && !snapshot.isDraggingOver && (
                      <div className="text-center text-xs text-gray-400 py-8">No leads</div>
                    )}
                    {capped.map((lead, idx) => (
                      <LeadCard key={lead.id} lead={lead} index={idx} onCardClick={onCardClick} columnKey={col.key} />
                    ))}
                    {provided.placeholder}
                    {hidden > 0 && (
                      <div className="mt-2 text-center text-[11px] text-gray-500 bg-white border border-dashed border-gray-300 rounded-md py-2" data-testid={`converted-hidden-${col.key}`}>
                        …and {hidden} more converted leads.{" "}
                        <button
                          onClick={() => changeCap("all")}
                          className="text-emerald-700 font-semibold hover:underline"
                        >
                          Show all
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default LeadsKanban;
